/**
 * Catálogo estático das feature flags: o que cada uma controla em cada plataforma.
 *
 * Fonte da verdade = código dos consumidores:
 * - WEB (web-gdv): `components/layout/Sidebar.tsx` (NAV_ITEMS) e `routes/index.tsx`.
 * - APP (mobile): `commons/FeatureFlags.ts` + `home/DsmReportTab/DsmUtilityScreen.tsx`.
 *
 * `applies` indica se a plataforma realmente lê a flag. Flags fora deste catálogo caem no
 * fallback da UI (campo `description` do Firestore).
 */
export type PlatformUsage = {
    /** A plataforma realmente consome esta flag? */
    applies: boolean
    /** O que a flag controla nesta plataforma (usado quando `applies`). */
    effect: string
}

export type FlagInfo = {
    summary: string
    app: PlatformUsage
    web: PlatformUsage
}

const noApp: PlatformUsage = { applies: false, effect: '' }
const noWeb: PlatformUsage = { applies: false, effect: '' }

export const FLAG_CATALOG: Record<string, FlagInfo> = {
    // ---- WEB-only: menus do Portal (web-gdv). Placeholder = tela ainda não existe ----
    NAV_CLIENTES: {
        summary: 'Menu "Clientes" do Portal.',
        app: noApp,
        web: {
            applies: true,
            effect: 'Menu lateral "Clientes" (/clientes). Placeholder — tela ainda não existe.',
        },
    },
    NAV_PEDIDOS: {
        summary: 'Menu "Pedidos" do Portal.',
        app: noApp,
        web: {
            applies: true,
            effect: 'Menu lateral "Pedidos" (/pedidos). Placeholder — tela ainda não existe.',
        },
    },
    NAV_PRODUTOS: {
        summary: 'Menu "Produtos" do Portal.',
        app: noApp,
        web: {
            applies: true,
            effect: 'Menu lateral "Produtos" (/produtos). Placeholder — tela ainda não existe.',
        },
    },
    NAV_ESTOQUE: {
        summary: 'Menu "Estoque" do Portal.',
        app: noApp,
        web: {
            applies: true,
            effect: 'Menu lateral "Estoque" (/estoque). Placeholder — tela ainda não existe.',
        },
    },
    NAV_NOTIFICACOES: {
        summary:
            'Menu "Notificações" do Portal. (O sino do app é controlado pela flag "NOTIFICACAO".)',
        app: noApp,
        web: {
            applies: true,
            effect: 'Menu lateral "Notificações" (/notificacoes). Placeholder — tela ainda não existe.',
        },
    },

    // ---- Relatórios: uma flag por relatório, aplicada a APP e WEB ----
    RELATORIO_PEDIDOS: {
        summary: 'Relatório de Pedidos (app + web).',
        app: {
            applies: true,
            effect: 'Aba DSM "Relatório de Pedidos". Tela existe.',
        },
        web: {
            applies: true,
            effect: 'Submenu "Relatórios › Pedidos". Tela existe.',
        },
    },
    RELATORIO_COMISSOES: {
        summary: 'Relatório de Comissões (app + web).',
        app: {
            applies: true,
            effect: 'Aba DSM "Relatório de Comissão". Tela existe.',
        },
        web: {
            applies: true,
            effect: 'Submenu "Relatórios › Comissões". Tela existe.',
        },
    },
    RELATORIO_BOLETOS: {
        summary: 'Relatório de Títulos/Boletos (app + web).',
        app: {
            applies: true,
            effect: 'Aba DSM "Relatório de Títulos / Boletos". Tela existe.',
        },
        web: {
            applies: true,
            effect: 'Submenu "Relatórios › Títulos/Boletos". Tela web existe',
        },
    },
    RELATORIO_CLIENTES: {
        summary: 'Relatório de Clientes (app + web).',
        app: {
            applies: true,
            effect: 'Aba DSM "Relatório de Clientes". Tela existe.',
        },
        web: {
            applies: true,
            effect: 'Submenu "Relatórios › Clientes". Tela web ainda não existe — manter desabilitado.',
        },
    },
    FICHA_CLIENTES: {
        summary: 'Ficha de Clientes (app + web).',
        app: {
            applies: true,
            effect: 'Aba DSM "Ficha de Clientes". Tela existe.',
        },
        web: {
            applies: true,
            effect: 'Submenu "Tarefas › Ficha de Clientes". Tela existe.',
        },
    },

    // ---- APP-only: features do DSM ----
    REGRAS_COMERCIAIS: {
        summary: 'Regras Comerciais (app DSM).',
        app: { applies: true, effect: 'Aba DSM "Regras Comerciais".' },
        web: noWeb,
    },
    TABELA_PRECOS: {
        summary: 'Tabela de Preços (app DSM).',
        app: { applies: true, effect: 'Aba DSM "Tabela de Preços".' },
        web: noWeb,
    },

    // ---- APP-only: auth / notificação ----
    NOTIFICACAO: {
        summary: 'Notificações do app.',
        app: {
            applies: true,
            effect: 'Sino/central de notificações (header e tela inicial).',
        },
        web: noWeb,
    },
    SENHA_COMISSAO_REDEFINIR: {
        summary: 'Redefinição de senha de comissão (app).',
        app: {
            applies: true,
            effect: 'Item de menu "Redefinir senha de comissão" (perfil DSM).',
        },
        web: noWeb,
    },
    SENHA_COMISSAO_RECUPERAR: {
        summary: 'Recuperação de senha de comissão (app).',
        app: {
            applies: true,
            effect: 'Opção "Esqueci senha de comissão" na tela de login.',
        },
        web: noWeb,
    },
}

export const getFlagInfo = (documentId: string): FlagInfo | undefined =>
    FLAG_CATALOG[documentId]
